import { DeleteFilled, PlusOutlined } from '@ant-design/icons';
import { gql } from '@apollo/client';
import { Button, Col, Form, Input, Row } from 'antd';
import FormLabel from 'components/FormLabel';
import MultilineInput from 'components/MultilineInput';
import { useMutation } from 'hooks/apollo';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import LicenseTag from 'components/LicenseTag';
import useUser from 'hooks/useUser';
import LanguageSelect from 'components/LanguageSelect';
import useDataSource from 'hooks/useDataSource';
import Page from 'components/Page';
import messages from '../messages';

const UPDATE_WEBSITE = gql`
  mutation($id: ID!, $input: WebsiteInput!) {
    updateWebsite(id: $id, input: $input) {
      id
    }
  }
`;

const extraFields = `
  website {
    id
    title
    url
    language
    languageName
    additionalLanguages {
      id
      language
      url
    }
    additionalUrls
    excludedUrls
    categories {
      id
    }
    cookiePrefix
    customCSS
  }
`;

function mapAdditionalLangugage({ id, language, url }) {
  return {
    id,
    language,
    url,
  };
}

const WebsiteSettings = () => {
  const { isSuperUser } = useUser();
  const { dataSource, loading, refetch } = useDataSource({ extraFields });
  const [updateWebsite, { loading: updating }] = useMutation(UPDATE_WEBSITE, { onCompleted: () => refetch() });
  const canEditCookiePrefix = dataSource?.activeLicense?.settings?.customCookiePrefix;

  const website = dataSource?.website;

  const initialValues = useMemo(() => (website && {
    ...website,
    additionalLanguages: website.additionalLanguages.map(mapAdditionalLangugage),
    categoryIds: website.categories.map(({ id }) => id),
  }), [website]);

  const onSubmit = (values) => updateWebsite({ variables: { id: website.id, input: values } });

  return (
    <Page loading={loading}>
      <Page.Content title="Website settings">
        <Form layout="vertical" onFinish={onSubmit} initialValues={initialValues}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="language"
                label={<FormLabel label={messages.language} description={messages.languageDescription} />}
              >
                <LanguageSelect allowOther />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="url"
                label={<FormLabel label={messages.url} description={messages.urlDescription} />}
                rules={[{ required: true, type: 'url' }]}
              >
                <Input placeholder="https://www.mydomain.com" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={<FormLabel label={messages.additionalLanguagesLabel} description={messages.additionalLanguagesDescription} />}>
            <Form.List name="additionalLanguages">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <Row key={field.key} gutter={16}>
                      <Col span={8}>
                        <Form.Item name={[field.name, 'language']}>
                          <LanguageSelect allowOther />
                        </Form.Item>
                      </Col>
                      <Col span={16}>
                        <Form.Item name={[field.name, 'url']} rules={[{ required: true, type: 'url' }]}>
                          <Input suffix={<DeleteFilled className="ant-btn-dangerous" onClick={() => remove(field.name)} />} />
                        </Form.Item>
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      <FormattedMessage {...messages.addLanguage} />
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>
          <Form.Item
            name="additionalUrls"
            label={<FormLabel label={messages.additionalUrlsLabel} description={messages.additionalUrlsDescription} />}
          >
            <MultilineInput placeholder={'Examples:\nhttps://test.mydomain.com\nhttps://stage.mydomain.com'} />
          </Form.Item>
          <Form.Item
            name="excludedUrls"
            label={<FormLabel label={messages.excludedUrlsLabel} description={messages.excludedUrlsDescription} />}
          >
            <MultilineInput placeholder={'Examples:\n/excluded/path\nquery=param\n(regex|works|too)$'} />
          </Form.Item>
          <Form.Item
            name="cookiePrefix"
            label={<FormLabel label={messages.cookiePrefix} description={messages.cookiePrefixDescription} extra={!canEditCookiePrefix && <LicenseTag setting="customCookiePrefix" showTooltip popoverProps={{ placement: 'topRight' }} />} />}
          >
            <Input disabled={!isSuperUser && !canEditCookiePrefix} />
          </Form.Item>
          <Form.Item
            name="customCSS"
            label={<FormLabel label={messages.customCSSLabel} description={messages.customCSSDescription} />}
          >
            <Input.TextArea autoSize placeholder={'Examples:\n.extellio-button { top: auto; bottom: 10%; }\n.extellio-button { background: red }'} />
          </Form.Item>

          <Page.Footer sticky>
            <Button type="primary" htmlType="submit" loading={updating}>
              Save
            </Button>
          </Page.Footer>
        </Form>
      </Page.Content>
    </Page>
  );
};

export default WebsiteSettings;
